import React, { useState } from 'react';
import { AlertTriangle, Upload, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdCreativesPage = () => {
  const [formData, setFormData] = useState({
    publisherName: '',
    offerName: '',
    callerId: '',
    facebookPostLink: ''
  });
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState('');

  const offerOptions = [
    'Appliance Repair',
    'Long Distance Moving',
    'Painter',
    'Junk Removal',
    'Locksmith',
    'Window Services',
    'Electrician',
    'Solar Services',
    'All / General Inquiry'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 3) {
      setError('Maximum 3 images allowed');
      return;
    }

    const validFiles = files.filter(file => {
      const isValid = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isValid) {
        setError('Only JPG and PNG files are allowed');
      }
      return isValid;
    });

    if (validFiles.length > 0) {
      setImages(validFiles);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitStatus(null);

    if (!formData.publisherName || !formData.offerName || !formData.callerId || !formData.facebookPostLink) {
      setError('Please fill all required fields');
      return;
    }

    if (images.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('publisher_name', formData.publisherName);
      formPayload.append('offer_name', formData.offerName);
      formPayload.append('caller_id', formData.callerId);
      formPayload.append('facebook_post_link', formData.facebookPostLink);
      
      images.forEach((image) => {
        formPayload.append('images', image);
      });

      const response = await fetch(`${API_URL}/api/ad-creatives/submit`, {
        method: 'POST',
        body: formPayload
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          publisherName: '',
          offerName: '',
          callerId: '',
          facebookPostLink: ''
        });
        setImages([]);
      } else {
        setError(result.detail || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold text-white text-center mb-6">
          Submit Ad Proof
        </h1>

        {/* Instructions */}
        <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-semibold mb-2">Important:</p>
              <ul className="text-yellow-400/90 text-sm space-y-1">
                <li>• Submit your Facebook ad proof after generating calls</li>
                <li>• Submission is required for payment</li>
                <li>• Fill all details correctly</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-green-400 font-medium">Submitted successfully</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Publisher Name */}
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Publisher Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="publisherName"
              value={formData.publisherName}
              onChange={handleInputChange}
              placeholder="Enter your name"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              data-testid="publisher-name-input"
            />
          </div>

          {/* Offer Name */}
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Offer Name <span className="text-red-400">*</span>
            </label>
            <select
              name="offerName"
              value={formData.offerName}
              onChange={handleInputChange}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              data-testid="offer-name-select"
            >
              <option value="">Select an offer</option>
              {offerOptions.map((offer) => (
                <option key={offer} value={offer}>{offer}</option>
              ))}
            </select>
          </div>

          {/* Caller ID */}
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Caller ID <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              name="callerId"
              value={formData.callerId}
              onChange={handleInputChange}
              placeholder="Phone number used for calls"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              data-testid="caller-id-input"
            />
          </div>

          {/* Facebook Post Link */}
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Facebook Post Link <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              name="facebookPostLink"
              value={formData.facebookPostLink}
              onChange={handleInputChange}
              placeholder="https://facebook.com/..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              data-testid="facebook-link-input"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Upload Images (Chat / Proof) <span className="text-red-400">*</span>
            </label>
            <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                multiple
                onChange={handleImageChange}
                className="hidden"
                id="image-upload"
                data-testid="image-upload-input"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">
                  Click to upload images
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  JPG, PNG only • Max 3 images
                </p>
              </label>
            </div>
            {images.length > 0 && (
              <div className="mt-3 space-y-2">
                {images.map((img, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-400 text-sm bg-slate-800 rounded px-3 py-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="truncate">{img.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdCreativesPage;
